import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Button from "../components/Button";
import LanguageSwitch from "../components/LanguageSwitch";
import SEO from "../components/SEO";
import { ReactComponent as SaudiRiyalSymbol } from "../assets/saudi_riyal_symbol.svg";
import { useLanguage } from "../contexts/LanguageContext";
import { getAttributionData } from "../utils/affiliateStorage";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { tiktokConsentId } from "../utils/tiktok";
import "../styles/Global.css";
import styles from "./Pricing.module.css";
import {
  rememberAuthReturn,
  rememberCheckoutReturn,
  safeInternalPath,
} from "../utils/conversionJourney";

/**
 * Pricing page component displaying three pricing tiers
 * @returns {JSX.Element} - Rendered pricing page
 */
const Pricing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { language, setLanguage, t } = useLanguage();
  const { user } = useAuthContext();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  // Dynamic pricing state (defaults in SAR)
  const [pricing, setPricing] = useState<{
    top_up: number;
    premium: number;
    premium_plus: number;
  }>({ top_up: 77, premium: 229, premium_plus: 449 });
  const [currency, setCurrency] = useState<"SAR" | "USD">("SAR");
  const [activePromo, setActivePromo] = useState<{
    code: string;
    discount_type: "percentage" | "fixed_amount";
    discount_value: number;
    ends_at: string | null;
  } | null>(null);
  const [countdown, setCountdown] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const paymentButtonRef = useRef<HTMLDivElement>(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
  const requestedPlan = searchParams.get("plan");
  const requestedLanguage = searchParams.get("lang");
  const postPurchaseReturn = safeInternalPath(searchParams.get("return_to"));

  useEffect(() => {
    if (requestedLanguage === "en" || requestedLanguage === "ar") {
      setLanguage(requestedLanguage);
    }
  }, [requestedLanguage, setLanguage]);

  useEffect(() => {
    if (requestedPlan === "premium_plus" && !selectedPlan) {
      setSelectedPlan(t.pricing.premiumPlus.name);
    }
  }, [requestedPlan, selectedPlan, t.pricing.premiumPlus.name]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // Fetch dynamic pricing based on user location
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pricing`);
        if (!res.ok)
          throw new Error(
            `Pricing API failed: ${res.status} ${res.statusText}`
          );
        const json = await res.json();

        if (json?.pricing) {
          setPricing({
            top_up: Math.round(json.pricing.top_up.amount * 100) / 100,
            premium: Math.round(json.pricing.premium.amount * 100) / 100,
            premium_plus:
              Math.round(json.pricing.premium_plus.amount * 100) / 100,
          });
          setCurrency(json.currency || json.pricing.top_up.currency || "SAR");
        }
        // Set active promo if exists
        if (json?.active_promo) {
          setActivePromo(json.active_promo);
        }
      } catch (err: any) {
        console.error("Pricing fetch error:", err);
      }
    };

    fetchPricing();
  }, [API_URL]);

  // Countdown timer effect — ticks every second when coupon has an end date
  useEffect(() => {
    if (!activePromo?.ends_at) {
      setCountdown(null);
      return;
    }

    const endTime = new Date(activePromo.ends_at).getTime();

    const tick = () => {
      const diff = endTime - Date.now();
      if (diff <= 0) {
        setCountdown(null);
        setActivePromo(null); // coupon expired — remove promo
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setCountdown({ days, hours, minutes, seconds });
    };

    tick(); // run immediately
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [activePromo?.ends_at]);

  // Helper to calculate discounted price
  const getFixedDiscountDisplayAmount = (): number => {
    if (!activePromo || activePromo.discount_type !== "fixed_amount") return 0;
    const sarAmount = activePromo.discount_value / 100;
    return currency === "SAR" ? sarAmount : sarAmount / 3.75;
  };

  const formatDisplayAmount = (amount: number): string => {
    const rounded = Math.round(amount * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  };

  const getDiscountedPrice = (originalPrice: number): number => {
    if (!activePromo) return originalPrice;
    if (activePromo.discount_type === "percentage") {
      return (
        Math.round(
          originalPrice * (1 - activePromo.discount_value / 100) * 100
        ) / 100
      );
    } else {
      return Math.max(
        0,
        Math.round(
          (originalPrice - getFixedDiscountDisplayAmount()) * 100
        ) / 100
      );
    }
  };

  // Helper to format promo message
  const getPromoMessage = (): string => {
    if (!activePromo) return "";
    if (activePromo.discount_type === "percentage") {
      return `Use code ${activePromo.code} for ${activePromo.discount_value}% off at checkout!`;
    } else {
      return `Use code ${activePromo.code} for ${currency === "SAR" ? "SAR " : "$"}${formatDisplayAmount(getFixedDiscountDisplayAmount())} off at checkout!`;
    }
  };

  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);

    setTimeout(() => {
      paymentButtonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 300);
  };

  const handleProceedToPayment = async () => {
    if (!selectedPlan) {
      (window as any).showNotification?.(
        "Please select a plan first",
        "warning"
      );
      return;
    }

    if (!user) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      rememberAuthReturn(returnTo);
      (window as any).showNotification?.("Sign in or create an account to continue", "info");
      navigate("/login", { state: { from: returnTo } });
      return;
    }

    const planSlug = (() => {
      const n = selectedPlan.toLowerCase();
      if (n.includes("top") || n.includes("شحن")) return "top_up";
      if (n.includes("plus") || n.includes("بلس")) return "premium_plus";
      return "premium";
    })();

    try {
      const attributionData = getAttributionData();

      const requestBody: any = {
        user_id: user.id,
        plan: planSlug,
        ...attributionData,
        tiktok_consent_id: tiktokConsentId(),
      };

      // Always let the backend calculate the authoritative SAR price.
      // For non-Saudi users we only display USD on the Pricing page –
      // invoices are still created in SAR, so we no longer send
      // amount_halala / currency overrides here.  This prevents rounding
      // mismatches that caused “invalid invoice” errors downstream.
      //
      // if (currency === "USD") {
      //   requestBody.amount_halala = Math.round(planPrice * 375);
      //   requestBody.currency = "SAR";
      // }

      const res = await authenticatedFetch(`${API_URL}/api/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) throw new Error("Failed to create invoice");
      const json = await res.json();

      const invoiceId = json.invoice?.id || json.id;

      if (postPurchaseReturn) {
        rememberCheckoutReturn(invoiceId, postPurchaseReturn);
      }

      if (json.applied && json.coupon_result) {
        (window as any).showNotification?.(
          `Coupon applied! ${json.coupon_result.message}`,
          "success"
        );
      }

      navigate(`/payment-confirmation?invoice_id=${invoiceId}`);
    } catch (err) {
      console.error(err);
      (window as any).showNotification?.("Error preparing payment", "error");
    }
  };

  const isMobile = window.innerWidth <= 768;

  // --- Derived savings for Premium Plus ---------------------------------
  // Compare the 3-month bundle with buying 3 separate Premium months.
  // Both the amount and percentage update with location-adjusted pricing.
  const monthsInPremiumPlus = 3;
  const premiumFullValueForBundle = pricing.premium * monthsInPremiumPlus;
  const premiumPlusSavingsAmount = Math.max(
    premiumFullValueForBundle - pricing.premium_plus,
    0
  );
  const premiumPlusSavingsPercent = Math.max(
    Math.round(
      (premiumPlusSavingsAmount / (premiumFullValueForBundle || 1)) * 100
    ),
    0
  );
  const premiumPlusEffectivePrice = getDiscountedPrice(pricing.premium_plus);
  const premiumPlusMonthlyPrice =
    premiumPlusEffectivePrice / monthsInPremiumPlus;

  return (
    <div className="page-container">
      <SEO
        title="C-Shaped | Pricing"
        description="Choose the right C-Shaped subscription: Top-up credits, Premium, or Premium Plus."
        canonical="https://c-shaped.com/pricing"
        ogTitle="C-Shaped Pricing"
        ogImage="https://c-shaped.com/logo.png"
      />
      <Header language={language} />
      <main className="main-content">
        <div className={styles.languageSwitchContainer}>
          <LanguageSwitch />
        </div>

        <div className={styles.pageTitle}>
          <h1 className={styles.pageTitleHeading}>{t.pricing.title}</h1>
        </div>

        {/* Promo banner with countdown timer */}
        {activePromo && (
          <div className={styles.promoBanner}>
            <div className={styles.promoBannerContent}>
              <span className={styles.promoMessage}>{getPromoMessage()}</span>
              {countdown && (
                <div className={styles.countdownContainer}>
                  <div className={styles.countdownSegment}>
                    <span className={styles.countdownNumber}>
                      {String(countdown.days).padStart(2, "0")}
                    </span>
                    <span className={styles.countdownLabel}>Days</span>
                  </div>
                  <span className={styles.countdownSeparator}>:</span>
                  <div className={styles.countdownSegment}>
                    <span className={styles.countdownNumber}>
                      {String(countdown.hours).padStart(2, "0")}
                    </span>
                    <span className={styles.countdownLabel}>Hrs</span>
                  </div>
                  <span className={styles.countdownSeparator}>:</span>
                  <div className={styles.countdownSegment}>
                    <span className={styles.countdownNumber}>
                      {String(countdown.minutes).padStart(2, "0")}
                    </span>
                    <span className={styles.countdownLabel}>Min</span>
                  </div>
                  <span className={styles.countdownSeparator}>:</span>
                  <div className={styles.countdownSegment}>
                    <span className={styles.countdownNumber}>
                      {String(countdown.seconds).padStart(2, "0")}
                    </span>
                    <span className={styles.countdownLabel}>Sec</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          className={
            isMobile ? styles.cardsContainerMobile : styles.cardsContainer
          }
        >
          {/* Top up Plan */}
          <div
            className={`
            ${isMobile ? styles.cardBaseMobile : styles.cardBase} 
            ${styles.cardTopup}
          `}
          >
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{t.pricing.topup.name}</h3>

              {/* Price with strikethrough when promo active */}
              {activePromo ? (
                <div className={styles.priceWithPromo}>
                  <div className={styles.originalPriceStrikethrough}>
                    {currency === "SAR" ? (
                      <SaudiRiyalSymbol className={styles.riyalSymbolSmall} />
                    ) : (
                      "$"
                    )}
                    {formatDisplayAmount(pricing.top_up)}
                  </div>
                  <div className={styles.discountedPriceRow}>
                    <div className={styles.cardPrice}>
                      {currency === "SAR" ? (
                        <SaudiRiyalSymbol className={styles.riyalSymbol} />
                      ) : (
                        "$"
                      )}
                      {formatDisplayAmount(getDiscountedPrice(pricing.top_up))}
                    </div>
                    <span className={styles.withCodeLabel}>with {activePromo.code}</span>
                  </div>
                </div>
              ) : (
                <div className={styles.cardPrice}>
                  {currency === "SAR" ? (
                    <SaudiRiyalSymbol className={styles.riyalSymbol} />
                  ) : (
                    "$"
                  )}
                  {formatDisplayAmount(pricing.top_up)}
                </div>
              )}

              <div className={styles.features}>
                {t.pricing.topup.features.map(
                  (feature: string, index: number) => (
                    <div key={index} className={styles.featureItem}>
                      <span className={styles.featureCheckmark}>✓</span>
                      <span className={styles.featureText}>{feature}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            <Button
              className={`${styles.selectButton} ${selectedPlan === t.pricing.topup.name
                ? styles.selectButtonTopupSelected
                : styles.selectButtonTopupUnselected
                }`}
              onClick={() => handlePlanSelect(t.pricing.topup.name)}
            >
              {selectedPlan === t.pricing.topup.name
                ? t.pricing.selected
                : t.pricing.select}
            </Button>
          </div>

          {/* Premium Plan (Most Popular) */}
          <div
            className={`
            ${isMobile ? styles.cardBaseMobile : styles.cardBase}
            ${isMobile ? styles.cardPremiumMobile : styles.cardPremium}
          `}
          >
            <div className={styles.badge}>{t.pricing.premium.badge}</div>

            <div className={styles.cardContent}>
              <h3 className={styles.cardTitleWhite}>
                {t.pricing.premium.name}
              </h3>

              {/* Price with strikethrough when promo active */}
              {activePromo ? (
                <div className={styles.priceWithPromo}>
                  <div className={styles.originalPriceStrikethroughWhite}>
                    {currency === "SAR" ? (
                      <SaudiRiyalSymbol
                        className={`${styles.riyalSymbolSmallWhite} riyal-symbol-white`}
                      />
                    ) : (
                      "$"
                    )}
                    {formatDisplayAmount(pricing.premium)}
                  </div>
                  <div className={styles.discountedPriceRow}>
                    <div className={styles.cardPriceWhite}>
                      {currency === "SAR" ? (
                        <SaudiRiyalSymbol
                          className={`${styles.riyalSymbolWhite} riyal-symbol-white`}
                        />
                      ) : (
                        "$"
                      )}
                      {formatDisplayAmount(getDiscountedPrice(pricing.premium))}
                    </div>
                    <span className={styles.withCodeLabelLight}>with {activePromo.code}</span>
                  </div>
                </div>
              ) : (
                <div className={styles.cardPriceWhite}>
                  {currency === "SAR" ? (
                    <SaudiRiyalSymbol
                      className={`${styles.riyalSymbolWhite} riyal-symbol-white`}
                    />
                  ) : (
                    "$"
                  )}
                  {formatDisplayAmount(pricing.premium)}
                </div>
              )}

              <div className={styles.validityLabel}>
                {t.pricing.premium.validFor}
              </div>

              <div className={styles.validityBadge}>
                {t.pricing.premium.validityPeriod}
              </div>

              <div className={styles.includesText}>
                {t.pricing.premium.includes}
              </div>

              <div className={styles.featuresPremium}>
                {t.pricing.premium.features.map(
                  (feature: string, index: number) => (
                    <div key={index} className={styles.featureItem}>
                      <span className={styles.featureCheckmarkYellow}>✓</span>
                      <span className={styles.featureTextWhite}>{feature}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            <Button
              className={`${styles.selectButton} ${selectedPlan === t.pricing.premium.name
                ? styles.selectButtonPremiumSelected
                : styles.selectButtonPremiumUnselected
                }`}
              onClick={() => handlePlanSelect(t.pricing.premium.name)}
            >
              {selectedPlan === t.pricing.premium.name
                ? t.pricing.selected
                : t.pricing.select}
            </Button>
          </div>

          {/* Premium Plus Plan (Best Value) */}
          <div
            className={`
            ${isMobile ? styles.cardBaseMobile : styles.cardBase}
            ${styles.cardPremiumPlus}
          `}
          >
            <div className={styles.premiumPlusBadge}>
              <span>{t.pricing.premiumPlus.badge}</span>
              <span className={styles.premiumPlusBadgeDivider} />
              <span>
                {premiumPlusSavingsPercent}%{" "}
                {t.pricing.premiumPlus.discountLabel}
              </span>
            </div>

            <div className={styles.cardContent}>
              <h3 className={styles.cardTitleWhite}>
                {t.pricing.premiumPlus.name}
              </h3>

              <p className={styles.premiumPlusDealHeadline}>
                {t.pricing.premiumPlus.dealHeadline}
              </p>

              <div className={styles.premiumPlusPricePanel}>
                <div className={styles.premiumPlusRegularValue}>
                  <span>{t.pricing.premiumPlus.regularValue}</span>
                  <span className={styles.premiumPlusRegularValuePrice}>
                    {currency === "SAR" ? (
                      <SaudiRiyalSymbol
                        className={`${styles.riyalSymbolSmallWhite} riyal-symbol-white`}
                      />
                    ) : (
                      "$"
                    )}
                    {formatDisplayAmount(premiumFullValueForBundle)}
                  </span>
                </div>

                {/* Price with strikethrough when promo active */}
                {activePromo ? (
                  <div className={styles.priceWithPromo}>
                    <div className={styles.originalPriceStrikethroughWhite}>
                      {currency === "SAR" ? (
                        <SaudiRiyalSymbol
                          className={`${styles.riyalSymbolSmallWhite} riyal-symbol-white`}
                        />
                      ) : (
                        "$"
                      )}
                      {formatDisplayAmount(pricing.premium_plus)}
                    </div>
                    <div className={styles.discountedPriceRow}>
                      <div className={styles.cardPriceWhite}>
                        {currency === "SAR" ? (
                          <SaudiRiyalSymbol
                            className={`${styles.riyalSymbolWhite} riyal-symbol-white`}
                          />
                        ) : (
                          "$"
                        )}
                        {formatDisplayAmount(premiumPlusEffectivePrice)}
                      </div>
                      <span className={styles.withCodeLabelLight}>
                        with {activePromo.code}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className={styles.cardPriceWhite}>
                    {currency === "SAR" ? (
                      <SaudiRiyalSymbol
                        className={`${styles.riyalSymbolWhite} riyal-symbol-white`}
                      />
                    ) : (
                      "$"
                    )}
                    {formatDisplayAmount(pricing.premium_plus)}
                  </div>
                )}

                <div className={styles.premiumPlusPerMonth}>
                  <span>{t.pricing.premiumPlus.perMonthPrefix}</span>
                  <strong>
                    {currency === "SAR" ? (
                      <SaudiRiyalSymbol
                        className={`${styles.riyalSymbolSmallWhite} riyal-symbol-white`}
                      />
                    ) : (
                      "$"
                    )}
                    {formatDisplayAmount(premiumPlusMonthlyPrice)}
                  </strong>
                  <span>{t.pricing.premiumPlus.perMonthSuffix}</span>
                </div>
              </div>

              <div className={styles.premiumPlusSavingsCallout}>
                <div className={styles.premiumPlusSavingsAmount}>
                  {t.pricing.premiumPlus.bonus}{" "}
                  {currency === "SAR" ? (
                    <>
                      <SaudiRiyalSymbol
                        className={`${styles.riyalSymbolSmallWhite} riyal-symbol-white`}
                      />
                      {formatDisplayAmount(premiumPlusSavingsAmount)}
                    </>
                  ) : (
                    `$${formatDisplayAmount(premiumPlusSavingsAmount)}`
                  )}
                </div>
              </div>

              <div className={styles.includesTextPremiumPlus}>
                {t.pricing.premiumPlus.includes}
              </div>

              <div className={styles.featuresPremiumPlus}>
                {t.pricing.premiumPlus.features.map(
                  (feature: string, index: number) => (
                    <div key={index} className={styles.featureItem}>
                      <span className={styles.featureCheckmarkYellow}>✓</span>
                      <span className={styles.featureTextWhite}>{feature}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            <Button
              className={`${styles.selectButton} ${selectedPlan === t.pricing.premiumPlus.name
                ? styles.selectButtonPremiumPlusSelected
                : styles.selectButtonPremiumPlusUnselected
                }`}
              onClick={() => handlePlanSelect(t.pricing.premiumPlus.name)}
            >
              {selectedPlan === t.pricing.premiumPlus.name
                ? t.pricing.selected
                : t.pricing.premiumPlus.cta}
            </Button>
            <div className={styles.premiumPlusReassurance}>
              {t.pricing.premiumPlus.reassurance}
            </div>
          </div>

          {/* Group Discount Card */}
          <div
            className={`
            ${isMobile ? styles.cardBaseMobile : styles.cardBase}
            ${styles.cardGroupDiscount}
          `}
          >
            <div className={styles.groupBadge}>{t.pricing.groupDiscount.title}</div>

            <div className={styles.cardContent}>
              <div className={styles.groupIconWrapper}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 20C17 18.3431 14.7614 17 12 17C9.23858 17 7 18.3431 7 20M21 17.0004C21 15.7702 19.7659 14.7129 18 14.25M3 17.0004C3 15.7702 4.2341 14.7129 6 14.25M18 10.2361C18.6137 9.68679 19 8.8885 19 8C19 6.34315 17.6569 5 16 5C15.2316 5 14.5308 5.28885 14 5.76389M6 10.2361C5.38625 9.68679 5 8.8885 5 8C5 6.34315 6.34315 5 8 5C8.76835 5 9.46924 5.28885 10 5.76389M12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11C15 12.6569 13.6569 14 12 14Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              <p className={styles.groupDescription}>
                {t.pricing.groupDiscount.description}
              </p>

              <div className={styles.features}>
                {t.pricing.groupDiscount.features.map(
                  (feature: string, index: number) => (
                    <div key={index} className={styles.featureItem}>
                      <span className={styles.featureCheckmark}>✓</span>
                      <span className={styles.featureText}>{feature}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            <div className={styles.groupContactButtons}>
              <a
                href="https://wa.me/966558096476?text=Hi%2C%20I%27m%20interested%20in%20group%20discount%20pricing"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.groupButtonPrimary}
              >
                {t.pricing.groupDiscount.whatsapp}
              </a>
              <a
                href="mailto:support@c-shaped.com?subject=Group%20Discount%20Inquiry"
                className={styles.groupButtonSecondary}
              >
                {t.pricing.groupDiscount.email}
              </a>
            </div>
          </div>
        </div>

        <div className={styles.paymentSection} ref={paymentButtonRef}>
          <Button
            className={`${styles.paymentButton} ${selectedPlan
              ? styles.paymentButtonSelected
              : styles.paymentButtonUnselected
              }`}
            onClick={handleProceedToPayment}
          >
            {t.pricing.proceedToPayment}
          </Button>
        </div>

        <div className={styles.termsSection}>
          {t.pricing.termsText}{" "}
          <a className={styles.termsLink} href="/terms">
            {t.pricing.termsLink}
          </a>
          .
        </div>
      </main>
      <Footer language={language} />
    </div>
  );
};

export default Pricing;
